import type { UIMessage, UITools } from "ai";
import { z } from "zod";
import type { ClientToolRequest } from "./client-tools";
import {
  appSurfaceValues,
  escalateReasons,
  type ProcessingStatus,
  processingStatusValues,
  reasoningLevels,
  routeMoves,
} from "./values";

export const messageErrorCodeSchema = z.enum([
  "stream_error",
  "aborted",
  "turn_timeout",
  "cost_limit",
  "setup_error",
  "workflow_start_failed",
  "empty_response",
  /**
   * The turn ended while a tool call was still streaming / awaiting a
   * result (a dangling `input-streaming` tool part), so the response was
   * cut off mid-tool-call rather than completing cleanly. Often coincides
   * with a provider `finishReason: "other"` (e.g. a mobile client
   * dropping the stream). Distinct from `empty_response`, where no
   * content streamed at all.
   */
  "incomplete_turn",
  /**
   * The agent loop hit `DEFAULT_TURN_LIMITS.maxSteps` while the model
   * still wanted to call more tools — the final step's tool inputs were
   * fully received from the provider (`input-available` state) but the
   * loop exited before they could execute. Distinct from `incomplete_turn`
   * (which captures *stream-level* termination mid-tool-input): here the
   * provider stream finished cleanly with `finishReason: "tool-calls"`
   * and we have a valid `gatewayGenerationId`. Sibling of `cost_limit`
   * and `turn_timeout` — a quota that fired, not a failure.
   */
  "step_limit",
  /** Per-turn auto-compaction cap reached without producing a tractable prompt. */
  "compaction_thrashing",
  /** Context-overflow recovery triggered compaction but `pickCutPoint` skipped. */
  "compaction_unactionable",
  // ─── Provider / gateway failure classifications ──────────────────────
  // Set by `apps/web/workflows/chat/classify-stream-error.ts` when the
  // thrown error carries enough signal to identify the upstream cause
  // (gateway typed errors or HTTP status). Lets the UI render a more
  // actionable banner than the generic `stream_error` fallback.
  /** Upstream provider returned 5xx, or the gateway couldn't reach a healthy backend. */
  "provider_unavailable",
  /** Upstream provider rate-limited the request (HTTP 429). */
  "provider_rate_limit",
  /** Upstream provider rejected auth (401/403) — credential/config issue. */
  "provider_auth_error",
  /** Upstream provider rejected the request payload (HTTP 400). */
  "provider_bad_request",
  /** Requested model isn't available at any allowed provider (HTTP 404). */
  "model_unavailable",
  /** Upstream provider exceeded the per-request timeout (408/504/network). */
  "provider_timeout",
]);

export type MessageErrorCode = z.infer<typeof messageErrorCodeSchema>;

/**
 * Custom `data` payload attached to file UIMessage parts when the user
 * uploads a file in chat. Persisted alongside the part and used by the
 * server to (a) annotate `<file-info />` tags with `upload-id`, and
 * (b) authorize `documentSearch({ uploadIds })` calls.
 *
 * `processingStatus` is retained on the part for backward compat with
 * persisted messages but is no longer surfaced to the model — processing
 * state is discovered on demand via `documentSearch`'s `skipped` array
 * (single source of truth, no stale-status race).
 */
export type FileUploadPartData = {
  uploadId?: string;
  processingStatus?: ProcessingStatus | null;
};

/**
 * Server-side projection of a UIMessage file part. Mirrors the shape that
 * `@ai-sdk` produces plus the custom `data` payload from
 * `apps/web/lib/chat-submission.ts`.
 */
export type FileUploadPart = {
  type: "file";
  filename?: string;
  mediaType?: string;
  url?: string;
  data?: FileUploadPartData;
};

const routeGateSchema = z.object({
  choice: z.enum(["yes", "no"]).nullable(),
  confidence: z.number().nullable(),
  bar: z.number(),
});

export const messageMetadataSchema = z.object({
  model: z.string().optional(),
  /**
   * Effective `ReasoningLevel` the turn ran at, for observability (ADR-084).
   * Typed as a plain string (like `model`) rather than a `z.enum` of the
   * levels: the level union is defined by the server model config, which the
   * contract must not import.
   */
  reasoningLevel: z.string().optional(),
  /** Aggregated token counts across all steps in the turn. */
  inputTokens: z.number().optional(),
  /**
   * Jev routing diagnostics (`models.auto-route.enable`): present when the
   * turn's Auto selection ran through the router. `routed` marks that a
   * routing evaluation happened; `routeFallback` says whether the router
   * decided ("none") or the turn stayed on its tier because the escalation
   * was refused or the router failed. Lets reporting slice spend by
   * routed-vs-fallback turns.
   */
  routed: z.boolean().optional(),
  /** TypeSafe confidence (distribution shape) of the escalate answer, yes or
   *  no — not top-option probability. Absent when the gate was not asked. */
  routeConfidence: z.number().optional(),
  /** Probability mass on the winning gate option. Compare with confidence. */
  routeTopProbability: z.number().optional(),
  /** TypeSafe confidence of the effort answer; below the effort bar the turn
   *  ran at the default effort. Absent when the router call failed. */
  routeEffortConfidence: z.number().optional(),
  routeLatencyMs: z.number().optional(),
  /** Time the turn blocked on the routing decision — the TTFT the router added. */
  routeWaitMs: z.number().optional(),
  /** Routing move this turn: escalated to high, forced off an ineligible
   *  tier, or stayed. */
  routeMove: z.enum(routeMoves).optional(),
  /** Why an escalation skipped the escalate gate: `"tool-error"` = a tool
   *  call on the previous turn failed. Absent on gate-decided escalations. */
  routeEscalateReason: z.enum(escalateReasons).optional(),
  /** Effort the router asked for when the model's routed-effort ceiling
   *  lowered it; `reasoningLevel` is what ran. */
  routeEffortCappedFrom: z.enum(reasoningLevels).optional(),
  /** The model of the tier the chat was on before this turn's routing
   *  decision. */
  routeFromModel: z.string().optional(),
  /** The escalate gate's answer, confidence, and the bar it had to clear. */
  routeGates: z
    .object({
      escalate: routeGateSchema.optional(),
    })
    .optional(),
  /**
   * `"none" | "low-confidence" | "timeout" | "gateway" | "zdr" | "parse" | "error" | "no-eligible"`.
   * `"error"` is a caller abort or an unclassified failure. A string, not an
   * enum: menu-router rows hold `"degenerate"`, and a parse failure would
   * drop the whole metadata object on those messages.
   */
  routeFallback: z.string().optional(),
  /** Routing tiers eligible this turn (1 or 2). With 1 there was nowhere
   *  to escalate, so only effort was asked. */
  routeEligibleTiers: z.number().optional(),
  /**
   * The raw picker selection (`"auto"` or a concrete model id; rows written
   * before Smart Auto merged into Auto may hold `"smart-auto"`). `model` is
   * the RESOLVED model the turn ran on; this preserves what the user
   * actually asked for so reporting can tell an "auto" turn apart from an
   * explicit pick of the same model.
   */
  modelSelection: z.string().optional(),
  outputTokens: z.number().optional(),
  totalTokens: z.number().optional(),
  reasoningTokens: z.number().optional(),
  /** Prompt-cache READ tokens (cheap, ~0.1x). Populated on cache hits. */
  cachedInputTokens: z.number().optional(),
  /** Prompt-cache WRITE/creation tokens (~1.25x). Non-zero only when this
   *  turn created/refreshed the cache. Lets us tell a cache write apart from
   *  a true miss when diagnosing why `cachedInputTokens` is 0. */
  cacheCreationInputTokens: z.number().optional(),
  /** Token counts from the last model call only (for context window fill). */
  lastStepInputTokens: z.number().optional(),
  lastStepOutputTokens: z.number().optional(),
  lastStepTotalTokens: z.number().optional(),
  lastStepReasoningTokens: z.number().optional(),
  lastStepCachedInputTokens: z.number().optional(),
  durationMs: z.number().optional(),
  stepCount: z.number().optional(),
  finishReason: z.string().optional(),
  generationId: z.string().optional(),
  /**
   * The AI Gateway's real **total billed** cost for this turn, in USD:
   * `providerMetadata.gateway.gatewayCost` (inference + surcharge) summed
   * across every step, plus any out-of-band compaction summarizer calls.
   * Absent when the gateway didn't report one (e.g. aborted before any step).
   *
   * NOTE: rows written before this became total-billed hold inference-only
   * cost. The surcharge is ~0.3% of a turn, so the discontinuity is
   * immaterial for aggregates and is not backfilled.
   */
  costUsd: z.number().optional(),
  /** Inference component of `costUsd` (`gateway.cost`), excluding surcharge.
   *
   *  The components do not always sum to `costUsd`: a step for which the
   *  gateway sent only the pre-summed total has a known total and an unknown
   *  split. `costUsd` is the billed figure of record. */
  costInferenceUsd: z.number().optional(),
  /** Surcharge component of `costUsd` (`gateway.surchargeCost`) — e.g. the
   *  reporting tag/user-id write meter. */
  costSurchargeUsd: z.number().optional(),
  referencedSkills: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        status: z.enum(["available", "activatable", "no_access", "deleted"]),
      })
    )
    .optional(),
  /**
   * Outcome of the turn. `"error"` indicates the assistant message was
   * persisted as a placeholder/partial because the run failed. Feedback
   * buttons use this to relax the usual "must have content" check.
   */
  status: z.enum(["success", "error"]).optional(),
  /** Structured failure classification; see `MessageErrorCode`. */
  errorCode: messageErrorCodeSchema.optional(),
  /** User-facing, sanitized error text. Safe to render directly. */
  errorMessage: z.string().optional(),
  /**
   * Raw diagnostic detail from the underlying thrown error — typically the
   * stack trace, or `name: message` when a stack isn't available. Populated
   * on error-path persistence for admin debugging only. Never rendered in
   * the chat UI; only surfaced in the admin feedback raw-metadata panel.
   */
  errorCause: z.string().optional(),
  /**
   * Workflow run ID that produced this assistant message. Used by chat debug
   * mode (and the feedback debug bundle) to correlate this turn with its
   * `workflow.workflow_steps` rows and `chat_metric` row. Populated on every
   * assistant-row persist (success and error paths); absent on user rows.
   *
   * Treated as a privileged internal identifier — never accepted from
   * clients, only ever read server-side for owner-scoped lookups.
   */
  runId: z.string().optional(),
  /**
   * Surface the message was produced on. Populated server-side from the
   * `message.appSurface` column when reading history; absent on legacy rows
   * written before per-row stamping. Used by the chat UI to render a
   * surface-switch divider between turns recorded on different surfaces.
   */
  appSurface: z.enum(appSurfaceValues).optional(),
  /**
   * Where in the host the user was when they sent this turn — the place, or
   * the page they were reading. Stamped server-side from the request's
   * `surfaceContext`; a value the client put on the message is dropped.
   */
  askedFrom: z
    .object({ label: z.string(), url: z.string().optional() })
    .optional(),
  /**
   * Author attribution for a shared Create room, rendered in the transcript
   * as who sent the turn.
   *
   * Stamped from the sender's session on append and stripped from inbound
   * metadata first, so these are read-only to clients even though they ride
   * the same wire schema as the fields a client does own. A room's transcript
   * is read by every member, and a private chat can later be promoted into
   * one, so values a client supplied must never reach the row.
   */
  authorId: z.string().optional(),
  authorName: z.string().optional(),
  collaborationRoom: z.boolean().optional(),
  /**
   * The live voice session this turn was spoken in (or answered during).
   * Set on both halves of a spoken exchange and on the session's recap row,
   * so the transcript can mark them and the recap can find its turns.
   */
  voiceSessionId: z.string().optional(),
  /**
   * What the live call had established when it delegated this turn. Shown
   * to the model for this turn only, never rendered in the transcript.
   */
  voiceCallContext: z.string().optional(),
});

export type MessageMetadata = z.infer<typeof messageMetadataSchema>;

/**
 * Drop the attribution a client sent, whatever it claimed: who sent the turn
 * and where it was asked from.
 *
 * Every path that persists a client-supplied message runs this first, so
 * "stamped from the session, never accepted from the wire" holds for all of
 * them and not just the one that remembered. Stripping is unconditional: a
 * private chat's forged values would otherwise sit on the row until
 * `openSharedRoom` promotes that chat and makes them team-visible.
 */
export function withoutClientAuthorship<T extends MessageMetadata | undefined>(
  metadata: T
): MessageMetadata {
  const {
    askedFrom: _askedFrom,
    authorId: _authorId,
    authorName: _authorName,
    collaborationRoom: _collaborationRoom,
    ...rest
  } = metadata ?? {};
  return rest;
}

/**
 * Rich shape of a skill companion file as carried in `data-skill-reference`.
 * Single source of truth — the type is derived from this schema and the
 * union in `dataPartsSchema["skill-reference"].skillFiles` reuses it, so
 * the validator and the type can never drift out of sync.
 */
export const skillReferenceFileSchema = z.object({
  uploadId: z.string(),
  filename: z.string(),
  path: z.string(),
  mediaType: z.string(),
  size: z.number(),
  processingStatus: z.enum(processingStatusValues).nullable().optional(),
});

export type SkillReferenceFile = z.infer<typeof skillReferenceFileSchema>;

/**
 * AI SDK data part for one user-uploaded document with Docling output
 * in `{storageKey}-meta/`. Used for processable types (PDF, Office,
 * text); images / unsupported types still flow as native file parts.
 *
 * Two field tiers (see ADR-055 for the full data flow):
 *   1. **Stable identity** — frozen at send time and persisted.
 *      Survives upload deletion so history can still name the file.
 *   2. **Volatile parse-quality** — stripped server-side by
 *      `hydrate-document-parts.ts` and re-resolved per-turn from the
 *      upload row, so the `<file-info />` tag reflects live Docling
 *      state rather than a send-time snapshot.
 */
export const documentDataPartDataSchema = z.object({
  uploadId: z.string(),
  filename: z.string(),
  mediaType: z.string(),
  /** Sandbox-relative path to the source file, e.g. `/home/user/current-chat/uploads/Q3.pdf`. */
  sandboxPath: z.string(),
  /** `${sandboxPath}-meta` — convenience handle so the model doesn't have to recompute it. */
  metaPath: z.string(),
  /**
   * Volatile. Accepted on the wire from the client (best-effort, from
   * upload polling) but stripped server-side before persistence. The
   * chat workflow's render pass resolves the authoritative value from
   * the upload row at prompt-build time.
   */
  pageCount: z.number().int().nonnegative().optional(),
  /** Volatile — same lifecycle as `pageCount`. */
  charCount: z.number().int().nonnegative().optional(),
  /** Volatile — same lifecycle as `pageCount`. */
  textLayerPresent: z.boolean().optional(),
  /** Volatile — same lifecycle as `pageCount`. */
  ocrApplied: z.boolean().optional(),
  /**
   * Filled in server-side at chat send. Read from `parsed.md` in the
   * upload's `-meta/` folder. Absent on the wire when the client emits
   * the part; never persisted back to the message (re-read on demand).
   */
  extractedMarkdown: z.string().optional(),
  /** Volatile — same lifecycle as `pageCount`. */
  processingStatus: z.enum(processingStatusValues).nullable().optional(),
  /**
   * Volatile — same lifecycle as `pageCount`. True when the rich
   * (tier-2) Docling pass failed but tier-1 markdown is still on disk.
   * Surfaced to the model in the `<file-info />` tag so it knows
   * OCR/tables weren't applied to that document.
   */
  richPassFailed: z.boolean().optional(),
});

export type DocumentDataPartData = z.infer<typeof documentDataPartDataSchema>;

export type DocumentDataPart = {
  type: "data-magnus-document";
  /**
   * Stable per-document id so the AI SDK can reconcile streamed updates
   * (e.g. `extracting-text` → `text-ready`). Constructed as
   * `magnus-doc:${uploadId}`.
   */
  id?: string;
  data: DocumentDataPartData;
};

/**
 * Type guard for `data-magnus-document` parts. Mirrors
 * {@link documentDataPartDataSchema} — every field required by the
 * schema is checked here so downstream consumers (`fileInfoForDocumentPart`,
 * `prepareMessagePartsForModel`, document hydration) can rely on the
 * non-null shape without re-validating. Optional fields (`extractedMarkdown`,
 * `pageCount`, `richPassFailed`, etc.) are intentionally not checked.
 *
 * Returning `false` on legacy persisted parts that pre-date a required
 * field is intentional: those parts are filtered out before render
 * rather than crashing the render with `undefined` dereferences.
 */
export function isDocumentDataPart(part: unknown): part is DocumentDataPart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (p.type !== "data-magnus-document") {
    return false;
  }
  const data = p.data;
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.uploadId === "string" &&
    typeof d.filename === "string" &&
    typeof d.mediaType === "string" &&
    typeof d.sandboxPath === "string" &&
    typeof d.metaPath === "string"
  );
}

/**
 * Verbatim user turn extracted by the compaction step (ADR-054 §VI.1).
 * File parts are unconditionally replaced with `<file-info />`.
 */
export const compactionVerbatimUserTurnSchema = z.object({
  messageId: z.string(),
  createdAt: z.string().optional(),
  text: z.string(),
});

export const compactionResumeFromSchema = z.object({
  userMessageId: z.string(),
  messageId: z.string(),
  partIndex: z.number().int().nonnegative(),
});

/**
 * Legacy span reference on compaction summaries written while the
 * ADR-051 reflog existed. New summaries omit it (ADR-130); the schema
 * stays so stored rows still validate. Bounds are ISO-8601 timestamps.
 */
export const compactionReflogReferenceSchema = z.object({
  /** `stepId` of the `compaction-boundary` JSONL entry. */
  stepId: z.string(),
  /** ISO-8601, inclusive. */
  coversFromCreatedAt: z.string().optional(),
  /** ISO-8601, inclusive. */
  coversToCreatedAt: z.string().optional(),
  /**
   * Legacy `jq` recall query against the sandbox reflog. Only on
   * summaries written before recall moved to `getConversation`; its
   * presence makes the renderer flag the summary's reflog mentions as
   * outdated.
   */
  jq: z.string().optional(),
});

/** `limit`: the call reached the maximum duration and was ended by the client. */
export const voiceSessionEndReasonValues = [
  "user",
  "idle",
  "error",
  "limit",
  /** The phone or another app took the audio session, or the app was backgrounded. */
  "interrupted",
] as const;

/** A chat turn the voice call handed to Magnus during the session. */
export const voiceSessionDelegationSchema = z.object({
  query: z.string(),
  /** The assistant thread message that answered it. */
  messageId: z.string().optional(),
  /** How many times the turn was submitted; 2 means one retry. */
  attempts: z.number().int().positive().optional(),
  /**
   * `timed-out` and `interrupted` mean the voice side stopped waiting; the
   * turn itself kept running in the thread.
   */
  status: z.enum([
    "completed",
    "failed",
    "timed-out",
    "interrupted",
    "awaiting-approval",
  ]),
});

/** One spoken turn, in order. Timestamps are ISO-8601. */
export const voiceSessionTurnSchema = z.object({
  role: z.enum(["user", "assistant"]),
  text: z.string(),
  at: z.string(),
  delegations: z.array(voiceSessionDelegationSchema).optional(),
});

export const dataPartsSchema = {
  sources: z.object({
    url: z.string(),
    title: z.string(),
    excerpt: z.string().optional(),
  }),
  thinking: z.object({
    content: z.string(),
    step: z.number().optional(),
  }),
  progress: z.object({
    current: z.number(),
    total: z.number(),
    message: z.string().optional(),
  }),
  "skill-reference": z.object({
    skillId: z.string(),
    skillName: z.string(),
    skillSlug: z.string(),
    skillDescription: z.string().optional(),
    skillBody: z.string().optional(),
    /**
     * Companion files for this skill, with the metadata the chat
     * workflow needs to emit `<file-info upload-id=… processing-status=… />`
     * tags so the model can pass concrete `uploadIds` to `documentSearch`.
     *
     * Accepts the legacy `string[]` shape (filenames only) so messages
     * persisted before the upload-id rollout still validate; the workflow
     * normalizes both shapes when rendering the prompt.
     */
    skillFiles: z
      .union([z.array(z.string()), z.array(skillReferenceFileSchema)])
      .optional(),
  }),
  /**
   * Chat-context compaction summary (ADR-054). Streams as `in-flight`
   * (shimmer) then `complete` (section break), or `skipped` (renders
   * nothing) when there was nothing to cut. Persisted on the synthetic
   * system row so reloads reproduce the boundary without re-running the
   * summarizer; `skipped` only ever lives on the streamed message.
   */
  "compaction-summary": z.object({
    status: z.enum(["in-flight", "complete", "skipped"]),
    /** Present once `status === "complete"`. */
    summary: z.string().optional(),
    /** Programmatically extracted — see `extractVerbatimUserTurns`. */
    verbatimUserTurns: z.array(compactionVerbatimUserTurnSchema).optional(),
    modelId: z.string().optional(),
    /** Inclusive. */
    coversFromMessageId: z.string().optional(),
    /**
     * Inclusive. With a `resumeFrom` whose `partIndex` is above 0 this is
     * `resumeFrom.messageId`, a row covered only up to that part.
     */
    coversToMessageId: z.string().optional(),
    /**
     * Newest message in history when this compaction ran. Rows after it
     * were written against the compacted context; token metadata on rows
     * up to it measured the pre-compaction prompt and is stale.
     */
    keptThroughMessageId: z.string().optional(),
    /**
     * Set when the cut fell inside the newest turn. The summary row sits
     * just before that turn's user message (`userMessageId`, kept
     * verbatim) and also replaces the turn's assistant rows before
     * `messageId` plus that row's parts before `partIndex` (a
     * `step-start` boundary). The model projection drops them.
     */
    resumeFrom: compactionResumeFromSchema.optional(),
    reflog: compactionReflogReferenceSchema.optional(),
  }),
  /**
   * Continuation nudge on a synthetic user row after a compaction
   * abort. Hidden from the UI; `promptText` is what the resumed model
   * actually sees.
   */
  "compaction-continuation": z.object({
    summaryMessageId: z.string(),
    promptText: z.string(),
  }),
  /**
   * One user-uploaded document with Docling output. See `DocumentDataPart`
   * for documentation. Emitted client-side from chat-input/chat-interface;
   * the server hydrates `extractedMarkdown` from `parsed.md` at send time.
   */
  "magnus-document": documentDataPartDataSchema,
  /**
   * The concrete model a routed Auto turn resolved to (Jev routing,
   * `models.auto-route.enable`). Emitted once per turn, inside the
   * assistant message envelope, so the composer can label the reply with
   * the real model while it streams. Live-only — the persistence view
   * drops `data-*` parts; stored history carries the model on
   * `metadata.model` instead. Only routed turns emit it, so `routed` is
   * always `true`; the field stays for clients that read it.
   */
  "model-resolved": z.object({
    modelId: z.string(),
    routed: z.boolean(),
  }),
  /**
   * Inline reference to a published artifact ({@link ArtifactReferencePart}).
   * The client sends `{ artifactId, artifactName, artifactSlug }`; the chat
   * route validates access + refreshes the name/slug. Unlike skill refs, no
   * heavy payload is persisted — the workflow fetches live binding specs at
   * render time so a republished artifact's contract is never stale.
   */
  "artifact-reference": z.object({
    artifactId: z.string(),
    artifactName: z.string(),
    artifactSlug: z.string(),
  }),
  /**
   * Marker attached by the meeting card's "Book this time" dialog (ADR-085)
   * alongside the full one-shot booking instruction text part. UI-only: its
   * presence makes the ENTIRE user message invisible in transcripts (no
   * bubble, no row chrome — the assistant's activity/approval card is the
   * next visible thing). The model is unaffected — `convertToModelMessages`
   * drops `data-*` parts (no `convertDataPart` option is passed anywhere),
   * so the complete instruction text reaches the model on the live turn and
   * on every history replay. The payload records what the form submitted,
   * for admin/debug introspection of the persisted parts.
   */
  "booking-form": z.object({
    /** Meeting title as entered on the form. */
    title: z.string(),
    /** Human-readable slot, e.g. `"Wed, Jul 29 · 2:00 – 2:30 PM CDT"`. */
    whenLabel: z.string(),
    /** Whether a Teams link was requested. */
    teams: z.boolean(),
    /** Whether a conference room was requested. */
    roomRequested: z.boolean(),
    /** How many files were attached to send with the invite. */
    attachmentCount: z.number().optional(),
  }),
  /**
   * Recap of a finished voice call, persisted once on hangup as an assistant
   * row whose only part this is. The transcript renders it as a divider chip
   * that opens the summary and the turn-by-turn transcript.
   */
  "voice-session": z.object({
    sessionId: z.string(),
    startedAt: z.string(),
    endedAt: z.string(),
    durationMs: z.number().nonnegative(),
    endReason: z.enum(voiceSessionEndReasonValues),
    turns: z.array(voiceSessionTurnSchema),
    /** Provider session id (GPT-Live `session.id`) for realtime sessions. */
    callId: z.string().optional(),
    summary: z.string().optional(),
  }),
};

export type SkillReferenceData = z.infer<
  (typeof dataPartsSchema)["skill-reference"]
>;

export type SkillReferencePart = {
  type: "data-skill-reference";
  data: SkillReferenceData;
};

export function isSkillReferencePart(
  part: unknown
): part is SkillReferencePart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  return (
    p.type === "data-skill-reference" &&
    typeof p.data === "object" &&
    p.data !== null &&
    typeof (p.data as Record<string, unknown>).skillId === "string"
  );
}

export type ArtifactReferenceData = z.infer<
  (typeof dataPartsSchema)["artifact-reference"]
>;

export type ArtifactReferencePart = {
  type: "data-artifact-reference";
  data: ArtifactReferenceData;
};

export function isArtifactReferencePart(
  part: unknown
): part is ArtifactReferencePart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  return (
    p.type === "data-artifact-reference" &&
    typeof p.data === "object" &&
    p.data !== null &&
    typeof (p.data as Record<string, unknown>).artifactId === "string"
  );
}

export type BookingFormData = z.infer<(typeof dataPartsSchema)["booking-form"]>;

export type BookingFormPart = {
  type: "data-booking-form";
  data: BookingFormData;
};

/**
 * Type guard for the booking-form marker part. Mirrors
 * `dataPartsSchema["booking-form"]` — the required fields the compact
 * transcript card renders are checked here so it never renders `undefined`.
 */
export function isBookingFormPart(part: unknown): part is BookingFormPart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (p.type !== "data-booking-form") {
    return false;
  }
  const data = p.data;
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.title === "string" &&
    typeof d.whenLabel === "string" &&
    typeof d.teams === "boolean" &&
    typeof d.roomRequested === "boolean"
  );
}

export type VoiceSessionTurn = z.infer<typeof voiceSessionTurnSchema>;
export type VoiceSessionEndReason =
  (typeof voiceSessionEndReasonValues)[number];
export type VoiceSessionData = z.infer<
  (typeof dataPartsSchema)["voice-session"]
>;

export type VoiceSessionPart = {
  type: "data-voice-session";
  data: VoiceSessionData;
};

export function formatVoiceDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}`
    : `${String(minutes).padStart(2, "0")}:${ss}`;
}

export type VoiceDelegationStatus = z.infer<
  typeof voiceSessionDelegationSchema
>["status"];

export const VOICE_DELEGATION_STATUS_LABEL: Record<
  VoiceDelegationStatus,
  string
> = {
  completed: "Answered",
  failed: "Didn't finish",
  "timed-out": "Kept running in chat",
  interrupted: "Stopped waiting",
  "awaiting-approval": "Needed approval",
};

/** The call's start as a local date and time; null when unparseable. */
export function formatVoiceSessionStartedAt(startedAt: string): string | null {
  const started = new Date(startedAt);
  if (Number.isNaN(started.getTime())) {
    return null;
  }
  return started.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function isVoiceSessionPart(part: unknown): part is VoiceSessionPart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (p.type !== "data-voice-session") {
    return false;
  }
  const data = p.data;
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.sessionId === "string" &&
    typeof d.durationMs === "number" &&
    Array.isArray(d.turns)
  );
}

/**
 * Whether a persisted message should be hidden from transcripts entirely —
 * no bubble, avatar, or action chrome.
 *
 * Today that means one thing: a user turn carrying the booking-form marker
 * (ADR-085). Its visible text is a machine-composed booking instruction the
 * user never typed, so the assistant's activity/approval card should be the
 * next visible thing. The message still persists and still reaches the model
 * unchanged; only the rendering is suppressed.
 *
 * Every transcript surface must agree, or a message hidden in chat reappears
 * in the shared view or the admin feedback detail. This is the rule they
 * share — call it instead of re-deriving the condition.
 */
export function isHiddenUserMessage(
  role: string,
  parts: readonly unknown[]
): boolean {
  return role === "user" && parts.some(isBookingFormPart);
}

/**
 * Runtime narrowing for UIMessage file parts. The AI SDK types `data` on
 * file parts as `unknown` (it doesn't know what callers attach), but in
 * Magnus the file-upload pipeline always attaches a `FileUploadPartData`
 * payload. Use this guard whenever you need to read `uploadId` /
 * `processingStatus` off a file part — don't widen the surrounding
 * generic constraint to leak the file-only `data` shape onto every part.
 */
export function isFileUploadPart(part: unknown): part is FileUploadPart {
  return (
    typeof part === "object" &&
    part !== null &&
    (part as { type?: unknown }).type === "file"
  );
}

export type CompactionVerbatimUserTurn = z.infer<
  typeof compactionVerbatimUserTurnSchema
>;
export type CompactionResumeFrom = z.infer<typeof compactionResumeFromSchema>;
export type CompactionReflogReference = z.infer<
  typeof compactionReflogReferenceSchema
>;
export type CompactionSummaryData = z.infer<
  (typeof dataPartsSchema)["compaction-summary"]
>;
export type CompactionContinuationData = z.infer<
  (typeof dataPartsSchema)["compaction-continuation"]
>;

export type CompactionSummaryPart = {
  type: "data-compaction-summary";
  data: CompactionSummaryData;
};

export type CompactionContinuationPart = {
  type: "data-compaction-continuation";
  data: CompactionContinuationData;
};

export function isCompactionSummaryPart(
  part: unknown
): part is CompactionSummaryPart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (p.type !== "data-compaction-summary") {
    return false;
  }
  const data = p.data;
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const status = (data as { status?: unknown }).status;
  return (
    status === "in-flight" || status === "complete" || status === "skipped"
  );
}

export function isCompactionContinuationPart(
  part: unknown
): part is CompactionContinuationPart {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (p.type !== "data-compaction-continuation") {
    return false;
  }
  const data = p.data;
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const d = data as { summaryMessageId?: unknown; promptText?: unknown };
  return (
    typeof d.summaryMessageId === "string" && typeof d.promptText === "string"
  );
}

export type CustomDataParts = {
  sources: z.infer<typeof dataPartsSchema.sources>;
  thinking: z.infer<typeof dataPartsSchema.thinking>;
  progress: z.infer<typeof dataPartsSchema.progress>;
  "skill-reference": SkillReferenceData;
  "artifact-reference": ArtifactReferenceData;
  "booking-form": BookingFormData;
  "voice-session": VoiceSessionData;
  "client-tool-request": ClientToolRequest;
  "compaction-summary": CompactionSummaryData;
  "compaction-continuation": CompactionContinuationData;
  "magnus-document": DocumentDataPartData;
  "model-resolved": z.infer<(typeof dataPartsSchema)["model-resolved"]>;
};

/**
 * The Magnus chat message shape with the tool set left generic. The server
 * (and in-repo consumers that need tool-part narrowing) instantiate it as
 * `MagnusUIMessage<MyTools>`; external SDK consumers use the default.
 */
export type MagnusUIMessage<TOOLS extends UITools = UITools> = UIMessage<
  MessageMetadata,
  CustomDataParts,
  TOOLS
>;
